import { SceneObject, Evidence } from '../types';
import { personColors } from '../data/mockData';

// Get person color by name (unique color for each person)
export const getPersonColor = (personName: string, allPeople?: string[]): string => {
  // If allPeople is provided, use it; otherwise create a default list
  const people = allPeople || [];
  const index = people.indexOf(personName);
  return personColors[index % personColors.length];
};

// Type guard functions
export const isSceneObject = (obj: SceneObject | Evidence): obj is SceneObject => {
  return 'people' in obj;
};

export const isEvidence = (obj: SceneObject | Evidence): obj is Evidence => {
  return 'description' in obj;
};

// Filter functions
export const filteredByTime = (obj: SceneObject | Evidence, startTimeRange: number, endTimeRange: number, getTimestampFromSlider: (value: number) => number): boolean => {
  const objTime = new Date(obj.time).getTime();
  const startTime = getTimestampFromSlider(startTimeRange);
  const endTime = getTimestampFromSlider(endTimeRange);
  return objTime >= startTime && objTime <= endTime;
};

export const filteredByCase = (obj: SceneObject | Evidence, currentCaseId: string): boolean => {
  if ('caseId' in obj) {
    return obj.caseId === currentCaseId;
  }
  return true; // Evidence doesn't have caseId, so show all evidence
};

export const filteredByAttributes = (obj: SceneObject | Evidence, attributeFilters: any): boolean => {
  if ('people' in obj) {
    // Check if any of the people in this object are filtered
    const hasFilteredPeople = obj.people.some(person => attributeFilters.people[person] === false);
    if (hasFilteredPeople) return false;
  }
  
  // Check location filter
  const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
  if (attributeFilters.location[locationName] === false) return false;
  
  // Check time filter
  const dateString = new Date(obj.time).toDateString();
  if (attributeFilters.time[dateString] === false) return false;
  
  return true;
};

export const filteredByLock = (obj: SceneObject | Evidence, attributeLock: any, getRelevantLocations: (attribute: string, value: string) => string[]): boolean => {
  if (!attributeLock.attribute || !attributeLock.value) return true;
  
  if (attributeLock.attribute === 'people') {
    if ('people' in obj) {
      return obj.people.includes(attributeLock.value);
    }
    return false;
  } else if (attributeLock.attribute === 'location') {
    const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
    // For location locks, show items that match the exact location OR are local locations within that global location
    if (locationName === attributeLock.value) return true;
    
    // If the locked location is a global location, also show local locations within it
    if (obj.location.mode === 'local' && obj.location.local?.parentLocation === attributeLock.value) {
      return true;
    }
    
    return false;
  } else if (attributeLock.attribute === 'time') {
    const objDateTime = new Date(obj.time).toISOString();
    return objDateTime === attributeLock.value;
  }
  
  return true;
};

// Get relevant locations for lock
export const getRelevantLocations = (attribute: string, value: string, sceneObjects: SceneObject[], currentCaseId: string): string[] => {
  const relevant: string[] = [];
  
  if (attribute === 'people') {
    // Get all locations where this person was present
    sceneObjects
      .filter(obj => 'people' in obj && obj.people.includes(value) && obj.caseId === currentCaseId)
      .forEach(obj => {
        const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
        if (!relevant.includes(locationName)) {
          relevant.push(locationName);
        }
      });
  } else if (attribute === 'location') {
    // Get all people who were at this location
    sceneObjects
      .filter(obj => {
        const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
        return locationName === value && obj.caseId === currentCaseId;
      })
      .forEach(obj => {
        if ('people' in obj) {
          obj.people.forEach((person: string) => {
            if (!relevant.includes(person)) {
              relevant.push(person);
            }
          });
        }
      });
  } else if (attribute === 'time') {
    // Get all people and locations for this time
    sceneObjects
      .filter(obj => new Date(obj.time).toISOString() === value && obj.caseId === currentCaseId)
      .forEach(obj => {
        if ('people' in obj) {
          obj.people.forEach((person: string) => {
            if (!relevant.includes(person)) {
              relevant.push(person);
            }
          });
        }
        const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
        if (!relevant.includes(locationName)) {
          relevant.push(locationName);
        }
      });
  }
  
  return relevant;
};

// Check if attribute is selectable
export const isAttributeSelectable = (attributeType: string, attributeValue: string, attributeLock: any, getRelevantLocations: (attribute: string, value: string) => string[], sceneObjects?: SceneObject[], currentCaseId?: string): boolean => {
  if (!attributeLock.attribute || !attributeLock.value) return true;
  
  if (attributeLock.attribute === attributeType) return true;
  
  // Special handling for location selection when a location is locked
  if (attributeType === 'location' && attributeLock.attribute === 'location') {
    // If the locked location is the same as the attribute value, it's selectable
    if (attributeLock.value === attributeValue) return true;
    
    // If this is a local location and the locked location is its parent, it's selectable
    if (sceneObjects && currentCaseId) {
      const isLocalUnderLockedMap = sceneObjects.some(obj => 
        obj.caseId === currentCaseId &&
        obj.location.mode === 'local' &&
        obj.location.local?.name === attributeValue &&
        obj.location.local?.parentLocation === attributeLock.value
      );
      if (isLocalUnderLockedMap) return true;
    }
  }
  
  // For other cases, use the getRelevantLocations function
  const relevantItems = getRelevantLocations(attributeLock.attribute, attributeLock.value);
  return relevantItems.includes(attributeValue);
};

